import { useEffect, useState } from 'react';
import { Button, Empty, ErrorNote, Field as FormField, Input, Panel, Select, relativeTime } from './ui';

/**
 * MQTT broker connections.
 *
 * Worth stating plainly in the UI rather than only in the docs: LoxLink runs on a public
 * host, so it can only reach a broker that is itself publicly reachable. A Mosquitto on a
 * home LAN is unreachable for the same NAT reason the whole design is pull-only — and
 * someone will otherwise spend an hour wondering why it will not connect.
 */

interface Broker {
  id: string;
  name: string;
  host: string;
  port: number;
  tls: boolean;
  username: string;
  enabled: boolean;
  connected: boolean;
  last_connected_at: string | null;
  last_error: string | null;
  messages_received: number;
  messages_published: number;
  live: { connected: boolean; topics: number };
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: init?.body ? { 'content-type': 'application/json' } : {},
  });
  const data = await res.json();
  if (!res.ok) throw new Error((data as { error?: string }).error ?? 'request failed');
  return data as T;
}

export function BrokersPanel({ canEdit }: { canEdit: boolean }) {
  const [brokers, setBrokers] = useState<Broker[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', host: '', port: 8883, tls: true, username: '', password: '' });

  const load = (): void => {
    void call<{ brokers: Broker[] }>('/api/brokers')
      .then((d) => {
        setBrokers(d.brokers);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  };

  useEffect(() => {
    load();
    // Connection state changes without us doing anything, so keep it current.
    const timer = setInterval(load, 10_000);
    return () => clearInterval(timer);
  }, []);

  async function create(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    try {
      await call('/api/brokers', { method: 'POST', body: JSON.stringify(form) });
      setCreating(false);
      setForm({ name: '', host: '', port: 8883, tls: true, username: '', password: '' });
      setTimeout(load, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Panel
      title="MQTT brokers"
      actions={canEdit && <Button variant="primary" onClick={() => setCreating(!creating)}>{creating ? 'Cancel' : 'Add broker'}</Button>}
    >
      <div className="text-xs text-warn bg-warn/10 border border-warn/30 rounded-md px-3 py-2 mb-3">
        LoxLink runs on a public host, so it can only reach a broker that is itself reachable
        from the internet — a cloud broker, or one you have deliberately exposed with TLS and
        a password. A broker on a home network cannot be reached, for the same reason
        LoxLink never connects to your Miniserver directly.
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {creating && (
        <form onSubmit={create} className="space-y-3 mb-4 pb-4 border-b border-ink-800">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="HiveMQ Cloud" required />
            </FormField>
            <FormField label="Host">
              <Input mono value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="broker.example.com" required />
            </FormField>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Port">
              <Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} />
            </FormField>
            <FormField label="TLS">
              <Select
                value={form.tls ? 'yes' : 'no'}
                onChange={(e) => setForm({ ...form, tls: e.target.value === 'yes', port: e.target.value === 'yes' ? 8883 : 1883 })}
              >
                <option value="yes">Yes (mqtts)</option>
                <option value="no">No (mqtt)</option>
              </Select>
            </FormField>
            <FormField label="Username">
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            </FormField>
          </div>
          <FormField label="Password" hint="Encrypted before storage, and never returned to the browser">
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
          </FormField>
          <Button type="submit" variant="primary">
            Add broker
          </Button>
        </form>
      )}

      {brokers === null ? (
        <Empty>Loading…</Empty>
      ) : brokers.length === 0 ? (
        <Empty>No brokers yet.</Empty>
      ) : (
        <div className="space-y-2">
          {brokers.map((b) => (
            <div key={b.id} className="border border-ink-800 rounded-md p-3">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block w-2 h-2 rounded-full shrink-0 ${b.live.connected ? 'bg-good' : 'bg-bad'}`}
                  title={b.live.connected ? 'connected' : 'not connected'}
                />
                <span className="text-sm text-ink-100">{b.name}</span>
                <span className="mono text-xs text-ink-500">
                  {b.tls ? 'mqtts' : 'mqtt'}://{b.host}:{b.port}
                </span>
                <span className="text-xs text-ink-500 ml-auto">
                  {b.live.topics} topic{b.live.topics === 1 ? '' : 's'} · {b.messages_received} in ·{' '}
                  {b.messages_published} out
                </span>
              </div>
              {b.last_error && <div className="text-xs text-bad mt-1">{b.last_error}</div>}
              {b.live.connected && b.last_connected_at && (
                <div className="text-xs text-ink-500 mt-1">connected {relativeTime(b.last_connected_at)}</div>
              )}
              {canEdit && (
                <div className="mt-2">
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (!confirm(`Delete broker "${b.name}"? Sources using it will stop receiving data.`)) return;
                      void call(`/api/brokers/${b.id}`, { method: 'DELETE' }).then(load);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
